/**
 * GA4 page-view tracking for the customer SPA.
 *
 * The gtag snippet in index.html sets `send_page_view: false`, so GA4 records
 * nothing on its own — every page view comes from here, fired on each resolved
 * route (see main.tsx). Without this, GA4 would only ever log the first load
 * and every client-side navigation would be invisible.
 *
 * PRIVACY: this is a financial product. Authenticated URLs embed record ids
 * (e.g. /service-request/<uuid>), and query strings can carry referral codes.
 * Both are stripped before anything is sent to Google — GA4 receives the route
 * SHAPE (/service-request/:id), never a customer's identifiers.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
/** Long digit runs are ids too (and can be phone-number-like). */
const LONG_NUMBER_RE = /\/\d{4,}(?=\/|$)/g;

/** Replace record identifiers in a path with placeholders. */
export function redactPath(pathname: string): string {
  return pathname.replace(UUID_RE, ':id').replace(LONG_NUMBER_RE, '/:id');
}

/**
 * Send one GA4 page_view for a route. Safe to call before gtag has loaded —
 * it simply no-ops rather than throwing (e.g. when an ad blocker is active).
 */
export function trackPageView(pathname: string): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  const path = redactPath(pathname);
  window.gtag('event', 'page_view', {
    page_path: path,
    // Rebuilt from origin + redacted path so the query string (referral codes,
    // tokens) never reaches GA4.
    page_location: `${window.location.origin}${path}`,
    page_title: document.title,
  });
}
